import { useState, useEffect } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import CustomerDashboard from "@/pages/CustomerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import PdaLogin from "@/pages/pda/PdaLogin";
import PdaMenu from "@/pages/pda/PdaMenu";
import PdaDispatchList from "@/pages/pda/PdaDispatchList";
import PdaDispatchDetail from "@/pages/pda/PdaDispatchDetail";
import PdaReceiving from "@/pages/pda/PdaReceiving";
import PdaLoading from "@/pages/pda/PdaLoading";
import PdaStocktake from "@/pages/pda/PdaStocktake";
import PdaRegister from "@/pages/pda/PdaRegister";
import PdaDelete from "@/pages/pda/PdaDelete";
import PdaIdentify from "@/pages/pda/PdaIdentify";
import BleDebug from "@/pages/BleDebug";
import PdaBarcodeTestHarness from "@/pages/PdaBarcodeTestHarness";
import PdaBagStepTestHarness from "@/pages/PdaBagStepTestHarness";

import { Toaster } from "@/components/ui/sonner";
import { supabase, syncTokenFromSession } from "@/lib/supabaseClient";
import { installAxiosInterceptors } from "@/lib/axiosSetup";
import { installApiCache, clearApiCache, prefetchApi } from "@/lib/apiCache";
import { APIProvider } from "@vis.gl/react-google-maps";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Install global axios behaviour once, at module load time
installAxiosInterceptors();
installApiCache();

async function fetchProfile(accessToken) {
  try {
    const resp = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      validateStatus: () => true,
    });
    if (resp.status >= 200 && resp.status < 300) return resp.data;
    return null;
  } catch (e) {
    return null;
  }
}

function App() {
  // Hydrate from localStorage synchronously so refreshes on protected routes
  // don't flash the login screen while the Supabase session is being verified.
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem("user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  // `bootChecking` only gates protected routes while we re-verify the session
  // in the background. The login screen renders immediately.
  const [bootChecking, setBootChecking] = useState(true);

  useEffect(() => {
    let mounted = true;

    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        syncTokenFromSession(session);
        const profile = await fetchProfile(session.access_token);
        if (mounted) {
          if (profile) {
            localStorage.setItem("user", JSON.stringify(profile));
            setUser(profile);
          } else {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            setUser(null);
          }
        }
      } else {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        if (mounted) setUser(null);
      }
      if (mounted) setBootChecking(false);
    })();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      syncTokenFromSession(session);
      if (event === "SIGNED_OUT" || !session) {
        localStorage.removeItem("user");
        setUser(null);
      }
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  // Warm the browser cache with the hot endpoints right after login so the
  // first tab opens feel instant.
  useEffect(() => {
    if (!user) return;
    const role = user.role || "customer";
    const hot = ["admin", "manager", "dispatcher", "driver"].includes(role)
      ? [`${API}/products`, `${API}/hotels`, `${API}/orders`]
      : [`${API}/products`, `${API}/orders`];
    prefetchApi(hot);
  }, [user]);

  const handleLogin = (userData, token) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      // best-effort
    }
    clearApiCache();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("impersonate_id");
    localStorage.removeItem("impersonate_name");
    sessionStorage.removeItem("dismissedAnnouncements");
    setUser(null);
  };

  const isAdminUser = (u) => {
    if (!u) return false;
    const role = u.role || "customer";
    return role === "admin" || role === "manager";
  };

  // Anyone who can operate the PDA UI: office staff + drivers. Drivers do NOT
  // get full-mode access — they're routed to /pda/menu on every entry.
  const isPdaEligible = (u) => {
    if (!u) return false;
    const role = u.role || "customer";
    return role === "admin" || role === "manager" || role === "driver";
  };
  const isDriverOnly = (u) => (u?.role === "driver");

  // Where should a freshly-logged-in user land? Drivers → PDA always.
  const landingRouteFor = (u) => {
    if (!u) return "/login";
    if (isDriverOnly(u)) return "/pda/menu";
    if (isAdminUser(u)) return "/admin";
    return "/dashboard";
  };

  // Lightweight loader used only when a protected route needs to wait for the
  // initial Supabase session check to finish (e.g. on a hard refresh).
  const BootLoader = () => (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="text-sm uppercase tracking-[0.2em] font-semibold text-neutral-500">Loading...</div>
    </div>
  );

  // Admin "login as customer" banner — shown whenever an impersonation
  // session is active (set from the Users tab).
  const impersonateId = localStorage.getItem("impersonate_id");
  const impersonateName = localStorage.getItem("impersonate_name");
  const exitImpersonation = () => {
    localStorage.removeItem("impersonate_id");
    localStorage.removeItem("impersonate_name");
    clearApiCache();
    window.location.assign("/admin");
  };

  return (
    <div className="App">
      <Toaster position="top-right" />
      {impersonateId && (
        <div
          className="sticky top-0 z-[100] flex items-center justify-center gap-3 bg-amber-400 text-amber-950 px-4 py-2 text-sm font-semibold shadow"
          data-testid="impersonation-banner"
        >
          <span>Logged in as <strong>{impersonateName || "customer"}</strong> (admin view)</span>
          <button
            onClick={exitImpersonation}
            className="rounded-md bg-amber-950 text-amber-50 px-3 py-1 text-xs font-bold hover:bg-amber-900 transition-colors"
            data-testid="exit-impersonation-btn"
          >
            Exit to Admin
          </button>
        </div>
      )}
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY || ""} libraries={["places"]}>
      <BrowserRouter>
        <Routes>
          <Route
            path="/reset-password"
            element={<ResetPassword />}
          />
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to={landingRouteFor(user)} replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              user && !isAdminUser(user) && !isDriverOnly(user) ? (
                <CustomerDashboard user={user} onLogout={handleLogout} />
              ) : user && isDriverOnly(user) ? (
                <Navigate to="/pda/menu" replace />
              ) : bootChecking ? (
                <BootLoader />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin"
            element={
              user && isAdminUser(user) ? (
                <AdminDashboard user={user} onLogout={handleLogout} />
              ) : user && isDriverOnly(user) ? (
                // Drivers can never enter the full-mode admin dashboard.
                <Navigate to="/pda/menu" replace />
              ) : bootChecking ? (
                <BootLoader />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* ─── PDA simplified routes (used by the Android WebView wrapper) ─── */}
          <Route path="/ble-debug" element={<BleDebug />} />
          <Route
            path="/pda/login"
            element={
              user && isPdaEligible(user) ? (
                <Navigate to="/pda/menu" replace />
              ) : (
                <PdaLogin onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/pda/menu"
            element={
              user && isPdaEligible(user) ? (
                <PdaMenu user={user} onLogout={handleLogout} />
              ) : bootChecking ? (
                <BootLoader />
              ) : (
                <Navigate to="/pda/login" replace />
              )
            }
          />
          <Route
            path="/pda/dispatch"
            element={user && isPdaEligible(user) ? <PdaDispatchList onLogout={handleLogout} /> : <Navigate to="/pda/login" replace />}
          />
          <Route
            path="/pda/dispatch/:orderId"
            element={user && isPdaEligible(user) ? <PdaDispatchDetail onLogout={handleLogout} /> : <Navigate to="/pda/login" replace />}
          />
          <Route
            path="/pda/receiving"
            element={user && isPdaEligible(user) ? <PdaReceiving onLogout={handleLogout} /> : <Navigate to="/pda/login" replace />}
          />
          <Route
            path="/pda/stocktake"
            element={user && isPdaEligible(user) ? <PdaStocktake onLogout={handleLogout} /> : <Navigate to="/pda/login" replace />}
          />
          <Route
            path="/pda/stocktake-hotel"
            element={user && isPdaEligible(user) ? <PdaStocktake mode="hotel" onLogout={handleLogout} /> : <Navigate to="/pda/login" replace />}
          />
          <Route
            path="/pda/register"
            element={user && isPdaEligible(user) ? <PdaRegister onLogout={handleLogout} /> : <Navigate to="/pda/login" replace />}
          />
          <Route
            path="/pda/delete"
            element={user && isPdaEligible(user) ? <PdaDelete onLogout={handleLogout} /> : <Navigate to="/pda/login" replace />}
          />
          <Route
            path="/pda/identify"
            element={user && isPdaEligible(user) ? <PdaIdentify onLogout={handleLogout} /> : <Navigate to="/pda/login" replace />}
          />
          <Route path="/pda" element={<Navigate to="/pda/login" replace />} />
          <Route
            path="/pda/loading"
            element={user && isPdaEligible(user) ? <PdaLoading onLogout={handleLogout} /> : <Navigate to="/pda/login" replace />}
          />
          <Route path="/test/pda-barcode" element={<PdaBarcodeTestHarness />} />
          <Route path="/test/pda-bag-step" element={<PdaBagStepTestHarness />} />
          <Route
            path="/"
            element={
              user ? (
                <Navigate to={landingRouteFor(user)} replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      </APIProvider>
    </div>
  );
}

export default App;
